import {
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  Image,
  useBreakpointValue,
  useToast,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { useAppDispatch } from "../../store/hooks/hooks";
import { addToCart } from "../../store/cartSlice/cartSlice";
import { useGetProductsQuery } from "../../productApi/productAPI";
import { normalizeProductForCart } from "../../utils/normalizeCartProduct";

export interface CarouselProduct {
  id: string;
  _id?: string;
  productId?: string;

  userId?: string;
  sellerUserId?: string;
  ownerId?: string;
  sellerId?: string;
  sellerBusinessName?: string;
  businessName?: string;
  sellerName?: string;
  shopName?: string;
  dispatchWarehouseId?: string;

  title: string;
  image?: string;
  imageUrl?: string;
  images?: string[];
  thumbnail?: string;
  description: string;
  category?: string;
  price?: number;

  selectedVariants?: any[];
  selectedPrice?: number;
  selectedImage?: string;
  resolvedImage?: string;

  isPhysical?: boolean;
  weightKg?: number;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  bulky?: boolean;

  [key: string]: any;
}

interface ProductCarouselProps {
  products: CarouselProduct[];
  slidesToShow?: {
    base?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
}

const getProductId = (product: Partial<CarouselProduct>) =>
  String(product._id || product.id || product.productId || "").trim();

const cardsBorderRadii = "8px";

const ProductCarousel: React.FC<ProductCarouselProps> = ({
  products,
  slidesToShow = { base: 1, md: 2, lg: 3 },
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const scrollRef = useRef<HTMLDivElement>(null);
  const slideCount = useBreakpointValue(slidesToShow);
  const slideWidth = slideCount ? `${100 / slideCount}%` : "100%";

  const [hasAutoScrolled, setHasAutoScrolled] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const { data: allProducts = [] } = useGetProductsQuery();

  const fullProductById = useMemo(() => {
    const map = new Map<string, any>();

    (allProducts || []).forEach((product: any) => {
      const id = getProductId(product);
      if (id) map.set(id, product);
    });

    return map;
  }, [allProducts]);

  const { theme } = useCustomTheme();
  const isDark = theme.name === "dark";

  const cardBg = theme.surface;
  const pageText = theme.text;
  const borderColor = theme.border;
  const borderStrong = theme.borderStrong;

  const panelShadow = theme.shadowMd;
  const cardShadow = theme.shadowSm;

  const primaryBtnBg = theme.gradients?.primaryButton || theme.primary;
  const primaryBtnHover =
    theme.gradients?.primaryButtonHover || theme.primaryHover;

  const getProductImage = (product: CarouselProduct) => {
    return (
      product.selectedImage ||
      product.imageUrl ||
      (Array.isArray(product.images) ? product.images.find(Boolean) : "") ||
      product.image ||
      product.thumbnail ||
      product.resolvedImage ||
      ""
    );
  };

  const handleAddToCart = (product: CarouselProduct, e: React.MouseEvent) => {
    e.stopPropagation();

    const productId = getProductId(product);
    const fullProduct = fullProductById.get(productId);

    const productForCart = {
      ...(fullProduct || {}),
      ...product,
    };

    const sellerId =
      productForCart.userId ||
      productForCart.sellerUserId ||
      productForCart.ownerId ||
      "";

    if (!sellerId) {
      toast({
        title: "Seller not found",
        description:
          "This product is missing seller information. Please open the product details and try again.",
        status: "warning",
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    const resolvedImage = getProductImage(productForCart);
    const resolvedPrice = Number(
      productForCart.selectedPrice ?? productForCart.price ?? 0,
    );

    dispatch(
      addToCart(
        normalizeProductForCart(productForCart, {
          id: productId,
          _id: productId,
          productId,
          image: resolvedImage,
          imageUrl: resolvedImage,
          selectedImage: resolvedImage,
          selectedVariants: Array.isArray(productForCart.selectedVariants)
            ? productForCart.selectedVariants
            : [],
          selectedPrice: resolvedPrice,
          price: resolvedPrice,
          quantity: 1,
        }),
      ),
    );

    toast({
      title: "Added to cart",
      description: "Product added to cart",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container || hasAutoScrolled) return;

    if (container.scrollWidth <= container.clientWidth) return;

    const scrollAmount = container.offsetWidth;
    const totalScrolls = Math.ceil(products.length / (slideCount || 1));
    let currentStep = 0;

    const interval = setInterval(() => {
      if (currentStep < totalScrolls) {
        container.scrollBy({ left: scrollAmount, behavior: "smooth" });
        currentStep++;
      } else {
        container.scrollTo({ left: 0, behavior: "smooth" });
        clearInterval(interval);
        setHasAutoScrolled(true);
      }
    }, 2500);

    return () => clearInterval(interval);
  }, [hasAutoScrolled, products.length, slideCount]);

  const updateScrollState = () => {
    const container = scrollRef.current;
    if (!container) return;

    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(
      container.scrollLeft + container.clientWidth < container.scrollWidth - 1,
    );
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    updateScrollState();

    container.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
      resizeObserver.disconnect();
    };
  }, [products.length, slideCount]);

  const scroll = (direction: "left" | "right") => {
    const container = scrollRef.current;
    if (!container) return;

    const scrollAmount = container.offsetWidth;
    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  return (
    <Box
      position="relative"
      borderRadius={cardsBorderRadii}
      bg={cardBg}
      boxShadow={cardShadow}
      border={`1px solid ${borderColor}`}
      p={{ base: 2, md: 3 }}
    >
      {canScrollLeft && (
        <Box
          position="absolute"
          left={2}
          top={2}
          bottom={2}
          width={{ base: "34px", md: "88px" }}
          zIndex={1}
          borderLeftRadius="16px"
          borderBottomLeftRadius="16px"
          borderTopLeftRadius="16px"
          bgGradient={
            isDark
              ? "linear(to-r, rgba(10,12,16,0.95) 0%, rgba(10,12,16,0.65) 35%, transparent 100%)"
              : "linear(to-r, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.75) 35%, transparent 100%)"
          }
          pointerEvents="none"
        />
      )}

      {canScrollRight && (
        <Box
          position="absolute"
          right={2}
          top={2}
          bottom={2}
          width={{ base: "34px", md: "88px" }}
          zIndex={1}
          borderRightRadius="16px"
          borderBottomRightRadius="16px"
          borderTopRightRadius="16px"
          bgGradient={
            isDark
              ? "linear(to-l, rgba(10,12,16,0.95) 0%, rgba(10,12,16,0.65) 35%, transparent 100%)"
              : "linear(to-l, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.75) 35%, transparent 100%)"
          }
          pointerEvents="none"
        />
      )}

      {canScrollLeft && (
        <IconButton
          aria-label="Scroll Left"
          icon={<ChevronLeftIcon boxSize={6} />}
          position="absolute"
          left={{ base: 3, md: 4 }}
          top="50%"
          transform="translateY(-50%)"
          zIndex={2}
          onClick={() => scroll("left")}
          variant="ghost"
          size="sm"
          color={"#fff"}
          bg={isDark ? "rgba(255,255,255,0.08)" : "rgba(24, 28, 42, 0.94)"}
          borderRadius={cardsBorderRadii}
          border={`1px solid ${borderStrong}`}
          backdropFilter="blur(8px)"
          boxShadow={panelShadow}
          _hover={{
            transform: "translateY(-50%) scale(1.03)",
            bg: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.78)",
          }}
          _active={{ transform: "translateY(-50%) scale(0.98)" }}
          transition="all 0.2s ease"
        />
      )}

      {canScrollRight && (
        <IconButton
          aria-label="Scroll Right"
          icon={<ChevronRightIcon boxSize={6} />}
          position="absolute"
          right={{ base: 3, md: 4 }}
          top="50%"
          transform="translateY(-50%)"
          zIndex={2}
          onClick={() => scroll("right")}
          variant="ghost"
          size="sm"
          color={"#fff"}
          bg={isDark ? "rgba(255,255,255,0.08)" : "rgba(24, 28, 42, 0.94)"}
          borderRadius={cardsBorderRadii}
          border={`1px solid ${borderStrong}`}
          backdropFilter="blur(8px)"
          boxShadow={panelShadow}
          _hover={{
            transform: "translateY(-50%) scale(1.03)",
            bg: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.78)",
          }}
          _active={{ transform: "translateY(-50%) scale(0.98)" }}
          transition="all 0.2s ease"
        />
      )}

      <Flex
        ref={scrollRef}
        overflowX="auto"
        scrollSnapType="x mandatory"
        gap={{ base: 2, md: 3 }}
        sx={{
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {products.map((product) => (
          <Box
            key={product.id}
            flex={`0 0 ${slideWidth}`}
            scrollSnapAlign="start"
            px={1}
          >
            <Box
              height={{ base: "220px", md: "240px" }}
              borderRadius={cardsBorderRadii}
              position="relative"
              overflow="hidden"
              boxShadow={panelShadow}
              border={`1px solid ${borderStrong}`}
              bg="#fff"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: theme.shadowMd,
              }}
              transition="all 0.2s ease"
            >
              <Box
                position="absolute"
                top={3}
                right={3}
                w="40px"
                h="40px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius={cardsBorderRadii}
                bg="rgba(24,28,42,0.94)"
                color="white"
                border="1px solid rgba(255,255,255,0.08)"
                boxShadow="0 2px 8px rgba(10,12,16,0.12)"
                zIndex={3}
                cursor="pointer"
                onClick={(e) => handleAddToCart(product, e)}
                _hover={{
                  bg: "rgba(32,37,56,0.98)",
                }}
                sx={{
                  "&:hover .carousel-top-plus": {
                    transform: "rotate(90deg)",
                  },
                }}
                transition="all 0.2s ease"
              >
                <Box
                  className="carousel-top-plus"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  transition="transform 0.25s ease"
                >
                  <Plus size={16} />
                </Box>
              </Box>

              <Image
                src={getProductImage(product) || undefined}
                alt={product.title}
                w="100%"
                h="100%"
                objectFit="contain"
                objectPosition="center"
                cursor="pointer"
                onClick={() => navigate(`/product/${product.id}`)}
              />

              <Box
                position="absolute"
                inset={0}
                bgGradient={
                  isDark
                    ? "linear(to-b, rgba(255,255,255,0.06), transparent 20%, transparent 100%)"
                    : "linear(to-b, rgba(255,255,255,0.22), transparent 25%, transparent 100%)"
                }
                pointerEvents="none"
              />

              <Box
                position="absolute"
                bottom="0"
                left="0"
                right="0"
                p={4}
                bgGradient="linear(to-t, rgba(0,0,0,0.82) 10%, rgba(0,0,0,0.32) 65%, transparent 100%)"
                color="white"
              >
                <Flex align="center" justify="space-between" gap={3}>
                  <Text
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="800"
                    lineHeight="1.1"
                    noOfLines={1}
                    flex="1"
                  >
                    {product.title}
                  </Text>

                  <Button
                    size="sm"
                    color="white"
                    bgImage={primaryBtnBg}
                    _hover={{
                      bgImage: primaryBtnHover,
                      transform: "translateY(-1px)",
                      boxShadow: theme.shadowMd,
                    }}
                    _active={{ transform: "translateY(0)" }}
                    borderRadius="4px"
                    px={4}
                    boxShadow={theme.shadowSm}
                    transition="all 0.2s ease"
                    onClick={() => navigate(`/product/${product.id}`)}
                    flexShrink={0}
                  >
                    View More
                  </Button>
                </Flex>
              </Box>
            </Box>
          </Box>
        ))}
      </Flex>
    </Box>
  );
};

export default ProductCarousel;
